import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    demoItemsDataRaw,
    demoTimelineEventsData,
    DEMO_CURRENCY_CONVERSION_RATES,
    DEMO_SNAPSHOTS,
    backdateDemoItems,
    backdateDemoLiquidAssets,
} from '../constants';

// Monetary fields, named once. `useDemoData`'s own conversion and D2's
// rescaling have to agree on this list or a currency switch would move some of
// an item's figures and not others.
export const DEMO_MONETARY_FIELDS = [
    'currentValue', 'mortgageInitialAmount', 'monthlyRentalIncome',
    'monthlyCosts', 'monthlyContribution', 'monthlyExpense',
    'monthlyIncome', 'loanAmount', 'mortgageMonthlyPayment',
];

// Timeline events carry a bare `amount` for one-off cash impacts on top of the
// item fields above.
const EVENT_MONETARY_FIELDS = [...DEMO_MONETARY_FIELDS, 'amount'];

const scaleFields = (source, fields, factor) => {
    const next = { ...source };
    fields.forEach((field) => {
        if (next[field] === null || next[field] === undefined || next[field] === '') return;
        const value = parseFloat(next[field]);
        if (!isNaN(value)) next[field] = value * factor;
    });
    return next;
};

const scaleEvent = (event, factor) => {
    const next = scaleFields(event, EVENT_MONETARY_FIELDS, factor);
    if (event.itemData) next.itemData = scaleFields(event.itemData, DEMO_MONETARY_FIELDS, factor);
    if (event.itemDataUpdate) next.itemDataUpdate = scaleFields(event.itemDataUpdate, DEMO_MONETARY_FIELDS, factor);
    return next;
};

/**
 * The demo portfolio.
 *
 * `sandbox` picks between two behaviours, and the difference matters:
 *
 *   false (the landing pages) — mirror the seed on every language or currency
 *     change. Nobody can edit those figures, so re-deriving from
 *     `constants.js` is always right.
 *
 *   true (D2, the app's demo mode) — the visitor can edit everything, locally.
 *     So the seed is applied ONCE and never rebuilt: a currency switch
 *     re-expresses what is on screen, and a language switch re-translates only
 *     the names nobody has replaced. Re-deriving here would silently discard
 *     five minutes of someone's typing.
 */
export const useDemoData = (currency, liquidAssets = 0, { sandbox = false } = {}) => {
    const { t } = useTranslation();
    const [demoItems, setDemoItems] = useState([]);
    const [demoTimelineEvents, setDemoTimelineEvents] = useState([]);

    // Memoize the translated and converted data to prevent unnecessary recalculations
    const processedData = useMemo(() => {
        const rate = DEMO_CURRENCY_CONVERSION_RATES[currency] || 1;
        const monetaryFields = DEMO_MONETARY_FIELDS;

        // Přeložíme a přepočítáme itemy portfolia
        const translatedDemoItems = demoItemsDataRaw.map(item => {
            const convertedItem = {
                ...item,
                name: t(item.nameKey)
            };
            monetaryFields.forEach(field => {
                if (convertedItem[field]) {
                    convertedItem[field] = parseFloat(convertedItem[field]) / rate;
                }
            });
            return convertedItem;
        });

        // Přeložíme a přepočítáme události v timeline
        const translatedDemoEvents = demoTimelineEventsData.map(event => {
            const translatedEvent = {
                ...event,
                eventName: t(event.eventNameKey)
            };
            
            if (event.itemData) {
                translatedEvent.itemData = { ...event.itemData };
                if (event.itemData.nameKey) {
                    translatedEvent.itemData.name = t(event.itemData.nameKey);
                }
                monetaryFields.forEach(field => {
                    if (translatedEvent.itemData[field]) {
                        translatedEvent.itemData[field] = parseFloat(translatedEvent.itemData[field]) / rate;
                    }
                });
            }
            if (event.itemDataUpdate) {
                translatedEvent.itemDataUpdate = { ...event.itemDataUpdate };
                 monetaryFields.forEach(field => {
                    if (translatedEvent.itemDataUpdate[field]) {
                        translatedEvent.itemDataUpdate[field] = parseFloat(translatedEvent.itemDataUpdate[field]) / rate;
                    }
                });
            }

            return translatedEvent;
        });

        return { translatedDemoItems, translatedDemoEvents };
    }, [t, currency]);

    // Sandbox bookkeeping: whether the seed has been applied for this session,
    // and which currency the in-memory figures are currently expressed in.
    const seededRef = useRef(false);
    const seedCurrencyRef = useRef(currency);

    const applySeed = useCallback(() => {
        setDemoItems(processedData.translatedDemoItems);
        setDemoTimelineEvents(processedData.translatedDemoEvents);
    }, [processedData]);

    // A session ends when sandbox mode goes away, so the next demo starts from
    // the example portfolio rather than the last visitor's edits.
    useEffect(() => {
        if (!sandbox) seededRef.current = false;
    }, [sandbox]);

    // Non-sandbox callers (the landing pages) keep the original behaviour.
    useEffect(() => {
        if (sandbox) return;
        applySeed();
    }, [sandbox, applySeed]);

    // D2 — sandbox seeds exactly once.
    useEffect(() => {
        if (!sandbox || seededRef.current) return;
        seededRef.current = true;
        seedCurrencyRef.current = currency;
        applySeed();
    }, [sandbox, currency, applySeed]);

    // D2 — a currency switch re-expresses the figures on screen (old rate →
    // new rate) instead of rebuilding them from the raw table, so an amount the
    // visitor typed moves with the rest instead of being thrown away.
    useEffect(() => {
        if (!sandbox || !seededRef.current) return;
        const previous = seedCurrencyRef.current;
        if (previous === currency) return;
        seedCurrencyRef.current = currency;
        const from = DEMO_CURRENCY_CONVERSION_RATES[previous] || 1;
        const to = DEMO_CURRENCY_CONVERSION_RATES[currency] || 1;
        const factor = from / to;
        if (factor === 1) return;
        setDemoItems(prev => prev.map(item => scaleFields(item, DEMO_MONETARY_FIELDS, factor)));
        setDemoTimelineEvents(prev => prev.map(event => scaleEvent(event, factor)));
    }, [sandbox, currency]);

    // D2 — a language switch re-translates only the names nobody has replaced.
    // `nameKey` is the marker: the demo mutators drop it the moment the visitor
    // edits an item, so its presence means "still the example's own name".
    useEffect(() => {
        if (!sandbox || !seededRef.current) return;
        setDemoItems(prev => prev.map(item => (item.nameKey ? { ...item, name: t(item.nameKey) } : item)));
        setDemoTimelineEvents(prev => prev.map(event => {
            const next = { ...event };
            if (event.eventNameKey) next.eventName = t(event.eventNameKey);
            if (event.itemData?.nameKey) next.itemData = { ...event.itemData, name: t(event.itemData.nameKey) };
            return next;
        }));
    }, [sandbox, t]);

    // What the demo portfolio looked like at each of the dates the seeded
    // snapshots carry. Built from the *live* items rather than the raw table, so
    // it follows the display currency — and from `liquidAssets` for the same
    // reason. See the note in constants.js for how far each value is walked back.
    //
    // D2 — in sandbox mode `useSnapshots` reads this ONCE, at seed time, and
    // rescales its own copies afterwards. So a later edit to a live item no
    // longer rewrites history, which is the correct reading of a snapshot
    // anyway: it records what was true then, not what the visitor has since
    // typed.
    const demoSnapshotHistory = useMemo(() => DEMO_SNAPSHOTS.map(snapshot => ({
        monthsAgo: snapshot.monthsAgo,
        items: backdateDemoItems(demoItems, snapshot),
        liquidAssets: backdateDemoLiquidAssets(liquidAssets, snapshot),
    })), [demoItems, liquidAssets]);

    return { demoItems, setDemoItems, demoTimelineEvents, setDemoTimelineEvents, demoSnapshotHistory };
};